/* eslint-disable no-param-reassign */
import L from 'leaflet';
import turf from '@turf/turf';
import { map, get } from 'lodash';

import { calculateHomographyMatrix } from '../homography-matrix-calculation';

/**
 * Calculates the 3D transformation matrix to apply to the video and returns it as a style option.
 *
 * @param {object} arg - The arguments for the function.
 * @param {object} arg.currentFeature - The current feature displayed on the video.
 * @param {object} arg.leafletMap - The leaflet map.
 * @param {object} arg.videoHeight - The height for the video player in the screen.
 * @param {object} arg.videoWidth - The width for the video player in the screen.
 * @returns {object} The style to be applied to the video.
 */
const getVideoStyle = ({ currentFeature, leafletMap, videoHeight = 0, videoWidth = 0 }) => {
  const originPoints = {
    x1: 0,
    y1: 0,
    x2: 0,
    y2: videoHeight,
    x3: videoWidth,
    y3: 0,
    x4: videoWidth,
    y4: videoHeight
  };

  const GPSCP = turf.getCoord(currentFeature);
  const coordinates = get(currentFeature, 'properties.coordinates', [GPSCP, GPSCP, GPSCP, GPSCP]);

  const geoJSONCoordinates = map(coordinates, coodinate => {
    const containerPoint = leafletMap.latLngToContainerPoint(
      new L.LatLng(coodinate[1], coodinate[0])
    );
    return [containerPoint.x, containerPoint.y];
  });

  const destinationPoints = {
    x1: geoJSONCoordinates[1][0],
    y1: geoJSONCoordinates[1][1],
    x2: geoJSONCoordinates[2][0],
    y2: geoJSONCoordinates[2][1],
    x3: geoJSONCoordinates[0][0],
    y3: geoJSONCoordinates[0][1],
    x4: geoJSONCoordinates[3][0],
    y4: geoJSONCoordinates[3][1]
  };

  const transformationMatrix = calculateHomographyMatrix({ originPoints, destinationPoints });

  const transformation4DMatrix = [
    transformationMatrix[0],
    transformationMatrix[3],
    0,
    transformationMatrix[6],
    transformationMatrix[1],
    transformationMatrix[4],
    0,
    transformationMatrix[7],
    0,
    0,
    1,
    0,
    transformationMatrix[2],
    transformationMatrix[5],
    0,
    transformationMatrix[8]
  ];

  const transformation3DMatrix = `matrix3d(${transformation4DMatrix.join(',')})`;

  const videoStyle = {
    '-webkit-transform-origin': '0 0',
    '-moz-transform-origin': '0 0',
    '-o-transform-origin': '0 0',
    transformOrigin: '0 0',
    '-webkit-transform': transformation3DMatrix,
    '-moz-transform': transformation3DMatrix,
    '-o-transform': transformation3DMatrix,
    transform: transformation3DMatrix
  };

  return videoStyle;
};

export default getVideoStyle;
